// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { useShellContext } from "@insite/client-framework/Components/IsInShell";
import { getSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getCurrentBillToState } from "@insite/client-framework/Store/Data/BillTos/BillTosSelectors";
import { useGetLink } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import FieldDefinition, { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import XngZone from "@pacific/App/Components/Common/XngZone";
import HeaderClickable, { HeaderClickableStyle } from "@pacific/App/Components/Header/HeaderClickable";
import { CustomAppState } from "@pacific/App/Store/Reducers";
import { xIsAuthenticated } from "@pacific/App/Store/Selectors/GeneralSelectors";
import { CartLinkZoneName, SignInZoneName } from "@pacific/XDL/constants";
import RecentPurchases from "@pacific/XDL/Icons/RecentPurchases";
import { xScreen } from "@pacific/XDL/Utilities";
import { getDataLayer } from "@pacific/XDL/Utilities/dataLayer";
import React from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const enum fields {
    showRecentPurchases = "showRecentPurchases",
    recentPurchasesLink = "recentPurchasesLink",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.showRecentPurchases]: boolean;
        [fields.recentPurchasesLink]: LinkFieldValue;
    };
}

const mapStateToProps = (state: CustomAppState) => {
    return {
        isAuthenticated: xIsAuthenticated(state),
        session: getSession(state),
        currentBillTo: getCurrentBillToState(state).value,
    };
};

const mapDispatchToProps = {};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & HasHistory & ResolveThunks<typeof mapDispatchToProps>;

interface HeaderActionsStyle {
    wrapper?: InjectableCss;
    recentPurchase?: HeaderClickableStyle;
}

const styles: HeaderActionsStyle = {
    wrapper: {
        css: css`
            display: flex;
            align-items: center;
            gap: 4px;
            ${xScreen("lte", "md")} {
                gap: 0;
            }
            &.cms-mode {
                flex-direction: column;
            }
        `,
    },
    recentPurchase: {
        clickable: {
            css: css`
                &.mobile-mode {
                    margin-left: 0;
                }
            `,
        },
    },
};

const HeaderActions = ({ fields, id, isAuthenticated, session, currentBillTo }: Props) => {
    const { showRecentPurchases, recentPurchasesLink } = fields;
    const isEditingInShell = useShellContext().isEditing;

    const recentPurchasesUrl = useGetLink(recentPurchasesLink);

    const _GaRecentPurchasesClick = () => {
        const dataLayer = getDataLayer();
        if (!dataLayer) {
            return;
        }

        const googleAnalyticsData = {
            event: "recent_purchases",
            click_text: translate("Recent Purchases"),
            destination: recentPurchasesUrl.url,
            authentication_state: isAuthenticated ? "Authenticated" : "Guest",
            affiliation: currentBillTo?.companyName || "",
            sales_rep: (session as any).salesperson?.name || "",
        };

        dataLayer.push(googleAnalyticsData);
    };

    return (
        <StyledWrapper {...styles.wrapper} className={isEditingInShell ? "cms-mode" : ""}>
            {isAuthenticated && showRecentPurchases && (
                <HeaderClickable
                    title={translate("Recent Purchases")}
                    titleBreakWords={true}
                    iconProps={{
                        src: RecentPurchases,
                    }}
                    {...(recentPurchasesUrl && { href: recentPurchasesUrl.url })}
                    onClick={_GaRecentPurchasesClick}
                    extendedStyles={styles.recentPurchase}
                />
            )}
            <XngZone contentId={id} zoneName={SignInZoneName} hint="Common/HeaderSignIn" />
            <XngZone contentId={id} zoneName={CartLinkZoneName} hint="Common/CartLink" />
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule<FieldDefinition> = {
    component: connect(mapStateToProps, mapDispatchToProps)(withHistory(HeaderActions)),
    definition: {
        group: "Xngage Custom" as any,
        fieldDefinitions: [
            {
                name: fields.showRecentPurchases,
                editorTemplate: "CheckboxField",
                fieldType: "General",
                defaultValue: true,
            },
            {
                name: fields.recentPurchasesLink,
                displayName: "Recent Purchases Link",
                editorTemplate: "LinkField",
                fieldType: "General",
                defaultValue: { type: "Page", value: "" },
                validate: (link: LinkFieldValue) => {
                    if (!link || !link.value) {
                        return "A value must be provided.";
                    }
                    return null;
                },
                isVisible: item => item.fields.showRecentPurchases,
            },
        ],
    },
};

export default widgetModule;
